/* @flow */
import Config from 'Config';

import apiRequest from 'lib/apiRequest';
import cookies from 'lib/Cookies';
import ChatActions from 'chat/actions/ChatActions.js';
import GoogleAPI from 'lib/GoogleAPI';
import PusherClient from 'pusher/PusherClient';
import Session from 'entities/Session';
import User from 'entities/User';
import ToastActions from 'actions/ToastActions';
import { dispatcher } from 'lib/Dispatcher';
import { getWindowUser } from 'lib/Startup';
import { promiseAppData } from 'promises/userBootData';
import { hardRedirectTo } from 'lib/RouteLogic';

import { ActionTypes } from './AuthConstants';

const SEC_IN_DAY = 60 * 60 * 24;

const AUTH_TOKEN = 'auth_token';
const REMEMBER_USER_TOKEN = 'remember_user_token';
const SESSION_UUID = 'session_uuid';

function clearNonStoreAuthState() {
    // XXX until we get our Flux in order
  ChatActions.resetPromiseMemoization();
  cookies.expire(AUTH_TOKEN, { domain: Config.Environment.API_PROXY_COOKIE_DOMAIN });
  cookies.expire(REMEMBER_USER_TOKEN, { domain: Config.Environment.API_PROXY_COOKIE_DOMAIN });
  cookies.expire(SESSION_UUID, { domain: Config.Environment.API_PROXY_COOKIE_DOMAIN });
  PusherClient.disconnectAndCleanUp();
}

export function onSessionCreated(user: User): Promise<User> {
  // Configure the userId in TrackJS
  const trackJs = window.trackJs;
  if (trackJs) {
    trackJs.configure({ userId: String(user.id) });
    // TJS should let us filter on application version, but it doesn't yet, so
    // they've advised using their generic filtering function
    trackJs.addMetadata('buildVersion', window._trackJs.version);
    // sent the whole thing as one value so we can filter by vectors of permissions
    trackJs.addMetadata('userPermissions', user.getPermissions());
    // like user.role but infers additional segments like "child"
    trackJs.addMetadata('userSegment', user.getTrackingSegment());
  }

  // Load user initial data before we dispatch AUTH_SUCCESS
  return promiseAppData(user).then(() => {
    dispatcher.dispatch({
      type: ActionTypes.AUTH_SUCCESS,
      user,
    });

    return user;
  });
}

const AuthActions = {
  signIn(email: string, password: string, persist: boolean = false): Promise<void> {
    const valid = email && email.length > 0 && password && password.length > 0;
    if (!valid) {
      return Promise.reject({ statusCode: 400 });
    }

    return Session.create(email, password, persist)
      .then(onSessionCreated, error => {
        if (error && error.code === 'unauthenticated') {
          console.warn('Session.create error', error);
        } else {
          console.error('Session.create error', error);
        }

        dispatcher.dispatch({
          type: ActionTypes.AUTH_FAILURE,
          error,
        });

        return Promise.reject(error);
      });
  },

  // TODO make Promise<void> or void
  accessTokenSignIn(token: string): Promise<User> {
    if (!(token.length > 0)) {
      console.error('accessTokenSignIn received bad token', token);
    }
    return Session.createWithToken(token).then(
      onSessionCreated,
      error => {
        dispatcher.dispatch({
          type: ActionTypes.AUTH_FAILURE,
          error,
        });

        return Promise.reject(error);
      }
    );
  },

  expireSession(): void {
    AuthActions.signOut('/session/expired');
  },

  signOut(redirectTo?: string = '/send_the_app'): Promise<void> {
    return Promise.all([
      Session.destroy(),
      GoogleAPI.signOut(),
    ]).then(() => {
      clearNonStoreAuthState();
      hardRedirectTo(redirectTo);
    })
    .catch((e) => {
      console.warn(e);
      console.error('signOut failed');
      ToastActions.showErrorToast('There was a problem logging out');
    });
  },

  requestPasswordReset(uid: string) {
    return apiRequest('/password', 'POST', {
      user_identifier: uid,
    });
  },

  checkLoggedIn(): Promise<User> {
    const user = getWindowUser();
    const userPromise = user ?
      Promise.resolve(user) :
      User.fetch();
    return userPromise.then(onSessionCreated);
  },

  jwtTokenSignIn(token: string) {
    dispatcher.dispatch({
      type: ActionTypes.TOKEN_ENTRY,
      token,
    });
  },

  googleSignIn(code: string) {
    return Session.createWithGoogleAuthorizationCode(code).then(onSessionCreated);
  },

  /**
   * Fetches and sets the token for autologin to native apps
   */
  setAuthTokenCookie(): void {
    apiRequest('/access_tokens', 'POST').then(response => {
      cookies.set(
        AUTH_TOKEN,
        response.token,
        { domain: Config.Environment.API_PROXY_COOKIE_DOMAIN, expires: 30 * SEC_IN_DAY },
      );
    }, () => {});
  },

  getAuthTokenFromCookies(): ?string {
    return cookies.get(AUTH_TOKEN);
  },
};

export default AuthActions;



// WEBPACK FOOTER //
// ./src/auth/AuthActions.js